/**
 * useFeatureAccess Hook
 * Checks if the current user has access to a feature based on their subscription tier
 * 
 * Usage:
 *   const { hasAccess, isLoading, requiredTier } = useFeatureAccess('voice_input');
 *   
 *   if (!hasAccess) {
 *     return <UpgradePrompt feature="voice_input" />;
 *   }
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { tierHasFeature, getMinimumTier, TIER_INFO } from '@/config/tierFeatures';

export const useFeatureAccess = (featureName) => {
  const { user, organization } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsLoading(false);
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/subscription/status`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setSubscriptionData(data);
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  // Determine current tier
  const getCurrentTier = () => {
    // OWNER ACCOUNTS: Always grant full access - never restrict
    const ownerEmails = ['todayskidz@comcast.net', 'todayskidz45@gmail.com'];
    const userEmail = localStorage.getItem('userEmail')?.toLowerCase();
    if (userEmail && ownerEmails.includes(userEmail)) {
      return 'signature'; // Owner always gets signature tier
    }
    
    // CHECK FOR viewAsTier: Staff members can be assigned to view as different tiers for testing
    // This is set by the owner via Owner Tools
    if (user?.viewAsTier) {
      return user.viewAsTier.toLowerCase();
    }
    
    // CRITICAL: If still loading or no data yet, grant full access to avoid lockout
    // This prevents false "basic" assignments during API latency
    if (!subscriptionData) return 'signature'; // Grant full access while loading
    
    // Signature tier always gets full access
    const plan = subscriptionData.plan?.toLowerCase();
    if (plan === 'signature') {
      return 'signature';
    }
    
    // Check if in trial period
    const isTrialActive = subscriptionData.status === 'trial' && 
                          subscriptionData.trialDaysRemaining > 0;
    
    if (isTrialActive) {
      return 'trial'; // Trial gets all features
    }

    // Trial ended without upgrade → lock everything until they pick a plan
    if (subscriptionData.status === 'trial' && subscriptionData.trialDaysRemaining <= 0) {
      return 'expired';
    }
    if (subscriptionData.status === 'expired' || subscriptionData.status === 'cancelled') {
      return 'expired';
    }

    // Paid subscription whose end date has passed → lock like an expired plan
    if (subscriptionData.isSubscriptionExpired) {
      return 'expired';
    }

    // Return the actual plan - only after we've confirmed subscription data loaded
    return plan || 'signature'; // Default to signature to prevent lockout
  };

  const currentTier = getCurrentTier();
  const requiredTier = getMinimumTier(featureName);
  const hasAccess = tierHasFeature(currentTier, featureName);
  const isTrialActive = currentTier === 'trial';
  const requiredTierInfo = TIER_INFO[requiredTier];

  return {
    hasAccess,
    isLoading,
    currentTier,
    requiredTier,
    requiredTierInfo,
    isTrialActive,
    subscriptionData
  };
};

/**
 * Hook to check multiple features at once
 */
export const useMultipleFeatureAccess = (featureNames) => {
  const { user, organization } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsLoading(false);
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/subscription/status`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setSubscriptionData(data);
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  const getCurrentTier = () => {
    // OWNER ACCOUNTS: Always grant full access - never restrict
    const ownerEmails = ['todayskidz@comcast.net', 'todayskidz45@gmail.com'];
    const userEmail = localStorage.getItem('userEmail')?.toLowerCase();
    if (userEmail && ownerEmails.includes(userEmail)) {
      return 'signature';
    }
    
    // CRITICAL: If still loading or no data yet, grant full access to avoid lockout
    if (!subscriptionData) return 'signature';
    
    // Signature tier always gets full access
    const plan = subscriptionData.plan?.toLowerCase();
    if (plan === 'signature') {
      return 'signature';
    }
    
    const isTrialActive = subscriptionData.status === 'trial' && 
                          subscriptionData.trialDaysRemaining > 0;
    
    if (isTrialActive) return 'trial';
    if (subscriptionData.status === 'expired' || subscriptionData.status === 'cancelled') return 'expired';
    if (subscriptionData.status === 'trial' && subscriptionData.trialDaysRemaining <= 0) return 'expired';
    if (subscriptionData.isSubscriptionExpired) return 'expired';
    return plan || 'signature'; // Default to signature to prevent lockout
  };

  const currentTier = getCurrentTier();

  const featureAccess = featureNames.reduce((acc, feature) => {
    acc[feature] = tierHasFeature(currentTier, feature);
    return acc;
  }, {});

  return {
    featureAccess,
    isLoading,
    currentTier,
    subscriptionData
  };
};

export default useFeatureAccess;
